import { RefObject, useCallback } from 'react';
import { Project } from '../types';
import { API_BASE_DEFAULT } from '../constants';
import { useSettings, useUI, useEditorState } from '../contexts';

type UseHeaderPropsParams = {
  serverState: 'unknown' | 'ok' | 'error';
  branches: string[];
  activeBranch: string;
  handleChangeBranch: (branch: string) => void;
  toggleAutosave: () => void;
  toggleTheme: () => void;
  projects: Project[];
  activeProject: string;
  projectOverride: string;
  setProjectOverride: (project: string) => void;
  handleApplyProjectOverride: (project: string) => void;
  handleCommit: () => void;
  handleExportSettings: () => void;
  triggerImport: () => void;
  handleImportSettings: (e: React.ChangeEvent<HTMLInputElement>) => void;
  importSettingsInputRef: RefObject<HTMLInputElement | null>;
  handleReloadProject: () => void;
  handleClearCache: () => void;
  handleSave: () => Promise<boolean>;
  handleRunAll: () => void;
};

export function useHeaderProps({
  serverState,
  branches,
  activeBranch,
  handleChangeBranch,
  toggleAutosave,
  toggleTheme,
  projects,
  activeProject,
  projectOverride,
  setProjectOverride,
  handleApplyProjectOverride,
  handleCommit,
  handleExportSettings,
  triggerImport,
  handleImportSettings,
  importSettingsInputRef,
  handleReloadProject,
  handleClearCache,
  handleSave,
  handleRunAll,
}: UseHeaderPropsParams) {
  // Get settings from context
  const { apiKey, apiSecret, apiBase, autoSaveEnabled, theme, setApiKey, setApiSecret, setApiBase } = useSettings();

  // Get UI state from context
  const { serverStateOverride, taskRunning, isFetchingTree } = useUI();

  // Get editor state from context
  const { commitMessage, setCommitMessage } = useEditorState();
  const onResetApiBase = useCallback(() => setApiBase(API_BASE_DEFAULT), [setApiBase]);
  const onApplyProjectOverride = useCallback(() => handleApplyProjectOverride(projectOverride), [handleApplyProjectOverride, projectOverride]);

  return {
    serverState: serverStateOverride || serverState,
    branches,
    activeBranch,
    onChangeBranch: handleChangeBranch,
    apiKey,
    apiSecret,
    apiBase,
    onChangeApiKey: setApiKey,
    onChangeApiSecret: setApiSecret,
    onChangeApiBase: setApiBase,
    onResetApiBase,
    autoSaveEnabled,
    onToggleAutosave: toggleAutosave,
    theme,
    onToggleTheme: toggleTheme,
    projects,
    activeProject,
    projectOverride,
    onChangeProject: handleApplyProjectOverride,
    onChangeProjectOverride: setProjectOverride,
    onApplyProjectOverride,
    commitMessage,
    onChangeCommitMessage: setCommitMessage,
    onCommit: handleCommit,
    onExportSettings: handleExportSettings,
    onImportSettings: triggerImport,
    onImportFileChange: handleImportSettings,
    importSettingsInputRef,
    onReloadProject: handleReloadProject,
    onClearCache: handleClearCache,
    onSave: handleSave,
    onRunAll: handleRunAll,
    taskRunning,
    isFetchingTree,
  };
}
